import React from "react";
import { useParams } from "react-router-dom";
import { Box, Typography } from "@mui/material";
import PageShell from "../components/layout/PageShell";
import DetailHero from "../features/detail/DetailHero";
import DetailPanels from "../features/detail/DetailPanels";
import DetailCTA from "../features/detail/DetailCTA";
import { FileCheck2, CalendarDays, GraduationCap, LayoutDashboard, ShieldCheck, BarChart3, QrCode } from "lucide-react";

const pages = {
  lms: {
    eyebrow: "Learning Management System",
    title: "A complete LMS for student progress, labs, and certifications.",
    copy: "Course progress, assignments, lab environments, certificates, and learning history in one unified student workspace.",
    features: ["Course Progress Tracking", "Lab Environments", "Certificate Generation", "Assignment Submission", "Mentor Connect", "Learning Analytics"],
    panels: [
      ["Student Dashboard", "Personal learning hub with progress rings, upcoming deadlines, and course roadmap.", GraduationCap],
      ["Lab Access", "Browser-based cloud labs for AWS, Azure, Linux, networking, and security practice.", ShieldCheck],
      ["Certificate System", "Auto-generated QR-verified certificates on course and certification completion.", QrCode],
    ],
  },
  "client-portal": {
    eyebrow: "Enterprise Client Portal",
    title: "A dedicated workspace for corporate training clients.",
    copy: "Team enrollments, skill gap reports, cohort management, invoice history, and training ROI dashboards for enterprise accounts.",
    features: ["Team Management", "Enrollment Dashboard", "Skill Reports", "Cohort Scheduling", "Invoice History", "ROI Analytics"],
    panels: [
      ["Team Enrollments", "Bulk enroll team members into cohorts and track their individual progress.", GraduationCap],
      ["Skill Gap Reports", "Visual skill maps showing team competency levels and recommended training paths.", BarChart3],
      ["Billing & Invoices", "Download invoices, manage subscriptions, and track training spend.", LayoutDashboard],
    ],
  },
  analytics: {
    eyebrow: "Analytics Platform",
    title: "Full-funnel analytics for revenue, leads, and learning outcomes.",
    copy: "Revenue tracking, lead pipeline analytics, student growth curves, course performance metrics, and placement conversion dashboards.",
    features: ["Revenue Analytics", "Lead Funnel Tracking", "Student Growth", "Course Performance", "Placement Metrics", "Custom Reports"],
    panels: [
      ["Revenue Dashboard", "Monthly recurring revenue, payment sources, refunds, and growth trends.", BarChart3],
      ["Lead Analytics", "Source quality, conversion rates, status pipeline, and follow-up performance.", LayoutDashboard],
      ["Learning Outcomes", "Completion rates, assessment scores, certification pass rates, and placement success.", GraduationCap],
    ],
  },
  certificate: {
    eyebrow: "Certificate Verification",
    title: "Blockchain-anchored, QR-verified digital certificates.",
    copy: "Every NETLABS certificate includes a unique QR code that links to a public verification page with student name, course, date, and authenticity proof.",
    features: ["QR Code Generation", "Public Verification URL", "Blockchain Anchoring", "Bulk Generation", "LinkedIn Integration", "Tamper Detection"],
    panels: [
      ["QR Verification", "Scan any certificate QR code to instantly verify authenticity on the public portal.", QrCode],
      ["Blockchain Anchor", "Certificate hash anchored to a public blockchain for permanent tamper-proof record.", ShieldCheck],
      ["LinkedIn Ready", "One-click add to LinkedIn profile with auto-filled credential details.", LayoutDashboard],
    ],
  },
  resources: {
    eyebrow: "Resource Center",
    title: "Guides, whitepapers, webinars, and certification prep materials.",
    copy: "A knowledge hub supporting SEO growth, certification demand, enterprise trust, and continuous lead nurturing through valuable content.",
    features: ["Certification Guides", "Whitepapers", "Webinar Recordings", "Practice Tests", "Career Blogs", "Tech Tutorials"],
    panels: [
      ["Content Library", "Searchable library of guides, PDFs, video tutorials, and practice materials.", FileCheck2],
      ["Webinar Platform", "Live and recorded webinars with registration, reminders, and replay access.", CalendarDays],
      ["Lead Nurturing", "Gated content downloads that feed qualified leads into the CRM pipeline.", LayoutDashboard],
    ],
  },
  events: {
    eyebrow: "Events Platform",
    title: "Live events, workshops, bootcamps, and webinar registrations.",
    copy: "Event listings with registration workflows, calendar sync, email reminders, and post-event resource distribution.",
    features: ["Event Listings", "Online Registration", "Calendar Sync", "Email Reminders", "Attendee Management", "Post-Event Resources"],
    panels: [
      ["Registration System", "Simple one-click registration with email confirmation and calendar invite.", CalendarDays],
      ["Attendee Dashboard", "Track registrations, check-ins, attendance, and post-event engagement.", LayoutDashboard],
      ["Event Analytics", "Registration sources, attendance rates, engagement scores, and follow-up conversions.", BarChart3],
    ],
  },
};

export default function DetailPage() {
  const { page } = useParams();
  const config = pages[page];

  if (!config) {
    return (
      <PageShell>
        <Box sx={{ py: 20, textAlign: "center" }}>
          <Typography variant="h4">Page not found</Typography>
        </Box>
      </PageShell>
    );
  }

  return (
    <PageShell>
      <DetailHero config={config} page={page} />
      <DetailPanels panels={config.panels} />
      <DetailCTA />
    </PageShell>
  );
}